import React, { useState, useCallback, useRef, useEffect } from 'react';
import { Helmet } from 'react-helmet-async';
import { 
  Database, 
  FileText, 
  Download, 
  Lock, 
  Unlock, 
  Settings, 
  Moon, 
  Sun, 
  Maximize, 
  Minimize, 
  BarChart3,
  Key,
  Upload,
  AlertCircle,
  CheckCircle,
  Eye,
  EyeOff,
  Trash2,
  Copy,
  RefreshCw,
  Clock,
  Zap,
  Table,
  Server,
  Shield,
  HardDrive,
  Layers,
  Grid,
  Filter,
  Hash
} from 'lucide-react';

interface AnalyticsData {
  operationsPerformed: number;
  totalTablesProcessed: number;
  totalRecordsProcessed: number;
  successRate: number;
  averageTime: number;
  totalDataSize: number;
}

interface SettingsData {
  databaseType: string;
  encryptionMethod: string;
  encryptionLevel: string;
  keyFormat: string;
  autoGenerate: boolean;
  showTimestamp: boolean;
  preserveSchema: boolean;
  maskSensitiveData: boolean;
}

interface ResultData {
  content: string;
  operation: string;
  timestamp: string;
  size: number;
  filename: string;
  tablesProcessed?: number;
  recordsProcessed?: number;
}

interface DatabaseEntry {
  name: string;
  size: number;
  type: string;
  content: string;
  tables?: TableInfo[];
}

interface TableInfo {
  name: string;
  records: number;
  columns: string[];
  sensitiveFields: string[];
}

interface DatabaseStructure {
  metadata: {
    originalType: string;
    originalName: string;
    encrypted: string;
    encryptionMethod: string;
    encryptionLevel: string;
    preserveSchema: boolean;
    maskSensitiveData: boolean;
    totalTables: number;
    totalRecords: number;
  };
  schema: TableInfo[] | null;
  encryptedData: { [key: string]: any };
}

const DatabaseEncryptDecrypt = () => {
  const [isDark, setIsDark] = useState<boolean>(false);
  const [isFullscreen, setIsFullscreen] = useState<boolean>(false);
  const [showSettings, setShowSettings] = useState<boolean>(false);
  const [showAnalytics, setShowAnalytics] = useState<boolean>(false);
  const [database, setDatabase] = useState<DatabaseEntry | null>(null);
  const [encryptedInput, setEncryptedInput] = useState<string>('');
  const [sqlInput, setSqlInput] = useState<string>('');
  const [password, setPassword] = useState<string>('');
  const [showPassword, setShowPassword] = useState<boolean>(false);
  const [operation, setOperation] = useState<'encrypt' | 'decrypt'>('encrypt');
  const [processing, setProcessing] = useState<boolean>(false);
  const [result, setResult] = useState<ResultData | null>(null);
  const [selectedTables, setSelectedTables] = useState<string[]>([]);
  const [analytics, setAnalytics] = useState<AnalyticsData>({
    operationsPerformed: 0,
    totalTablesProcessed: 0,
    totalRecordsProcessed: 0,
    successRate: 100,
    averageTime: 0,
    totalDataSize: 0
  });
  const [settings, setSettings] = useState<SettingsData>({
    databaseType: 'MySQL',
    encryptionMethod: 'AES-256',
    encryptionLevel: 'table-level',
    keyFormat: 'password',
    autoGenerate: false,
    showTimestamp: true,
    preserveSchema: true,
    maskSensitiveData: true
  });

  const containerRef = useRef<HTMLDivElement>(null);
  const fileInputRef = useRef<HTMLInputElement>(null);

  // Unicode-safe base64 encoding/decoding
  const encodeUnicode = (str: string): string => {
    return btoa(encodeURIComponent(str).replace(/%([0-9A-F]{2})/g, (match, p1) => {
      return String.fromCharCode(parseInt(p1, 16));
    }));
  };

  const decodeUnicode = (str: string): string => {
    return decodeURIComponent(atob(str).split('').map(c => {
      return '%' + ('00' + c.charCodeAt(0).toString(16)).slice(-2);
    }).join(''));
  };

  // Generate secure password
  const generatePassword = useCallback((): string => {
    const chars = 'ABCDEFGHIJKLMNOPQRSTUVWXYZabcdefghijklmnopqrstuvwxyz0123456789!@#$%^&*';
    let password = '';
    for (let i = 0; i < 20; i++) {
      password += chars.charAt(Math.floor(Math.random() * chars.length));
    }
    return password;
  }, []);

  // Database file handling
  const handleDatabaseUpload = useCallback(async (event: React.ChangeEvent<HTMLInputElement>) => {
    const uploadedFile = event.target.files?.[0];
    if (!uploadedFile) return;

    const content = await new Promise<string>((resolve) => {
      const reader = new FileReader();
      reader.onload = () => resolve(reader.result as string);
      reader.readAsText(uploadedFile);
    });
    
    // Analyze database structure
    const tables = analyzeDatabaseStructure(content, uploadedFile.name);
    
    setDatabase({
      name: uploadedFile.name,
      size: uploadedFile.size,
      type: detectDatabaseType(uploadedFile.name, content),
      content: content,
      tables: tables
    });
    
    setSelectedTables(tables.map(t => t.name));
  }, []);

  // Detect database type
  const detectDatabaseType = (filename: string, content: string): string => {
    const ext = filename.toLowerCase().split('.').pop();
    const contentLower = content.toLowerCase();
    
    if (ext === 'sql' || contentLower.includes('create table') || contentLower.includes('insert into')) {
      if (contentLower.includes('mysql') || contentLower.includes('auto_increment')) return 'MySQL';
      if (contentLower.includes('postgresql') || contentLower.includes('serial')) return 'PostgreSQL';
      if (contentLower.includes('sqlite')) return 'SQLite';
      return 'SQL';
    }
    if (ext === 'json' || content.trim().startsWith('{') || content.trim().startsWith('[')) return 'MongoDB';
    if (ext === 'csv') return 'CSV';
    if (ext === 'xml') return 'XML';
    return 'Unknown';
  };

  // Analyze database structure
  const analyzeDatabaseStructure = (content: string, filename: string): TableInfo[] => {
    const tables: TableInfo[] = [];
    const dbType = detectDatabaseType(filename, content);
    
    if (dbType === 'MongoDB' || content.trim().startsWith('{')) {
      try {
        const data = JSON.parse(content);
        if (Array.isArray(data)) {
          tables.push({
            name: 'collection_main',
            records: data.length,
            columns: data.length > 0 ? Object.keys(data[0]) : [],
            sensitiveFields: []
          });
        } else if (typeof data === 'object' && data !== null) {
          Object.keys(data).forEach(key => {
            const value = (data as any)[key];
            if (Array.isArray(value)) {
              tables.push({
                name: key,
                records: value.length,
                columns: value.length > 0 && typeof value[0] === 'object' ? Object.keys(value[0]) : [],
                sensitiveFields: []
              });
            }
          });
        }
      } catch {
        tables.push({ name: 'unknown_collection', records: 0, columns: [], sensitiveFields: [] });
      }
    } else if (dbType.includes('SQL') || content.includes('CREATE TABLE')) {
      const tableMatches = content.match(/CREATE TABLE\s+`?(\w+)`?\s*\(/gi);
      if (tableMatches) {
        tableMatches.forEach(match => {
          const tableName = match.match(/CREATE TABLE\s+`?(\w+)`?/i)?.[1] || 'unknown';
          const insertCount = (content.match(new RegExp(`INSERT INTO\\s+\`?${tableName}\`?`, 'gi')) || []).length;
          
          // Extract columns from CREATE TABLE statement
          const tableDefMatch = content.match(new RegExp(`CREATE TABLE\\s+\`?${tableName}\`?\\s*\\(([^;]+)\\)`, 'is'));
          const columns: string[] = [];
          if (tableDefMatch) {
            const columnDefs = tableDefMatch[1].split(',');
            columnDefs.forEach(def => {
              const colMatch = def.trim().match(/^\`?(\w+)\`?/);
              if (colMatch) columns.push(colMatch[1]);
            });
          }
          
          tables.push({
            name: tableName,
            records: insertCount * 10, // Estimate
            columns: columns,
            sensitiveFields: columns.filter(col => 
              ['password', 'email', 'phone', 'ssn', 'credit_card', 'address'].some(sensitive => 
                col.toLowerCase().includes(sensitive)
              )
            )
          });
        });
      }
    } else if (dbType === 'CSV') {
      const lines = content.split('\n').filter(line => line.trim());
      if (lines.length > 0) {
        const headers = lines[0].split(',').map(h => h.trim().replace(/"/g, ''));
        tables.push({
          name: 'csv_data',
          records: lines.length - 1,
          columns: headers,
          sensitiveFields: headers.filter(col => 
            ['password', 'email', 'phone', 'ssn', 'credit', 'address'].some(sensitive => 
              col.toLowerCase().includes(sensitive)
            )
          )
        });
      }
    }
    
    return tables;
  };

  // Analytics tracking
  const trackOperation = useCallback((type: string, success: boolean, tablesCount: number, recordsCount: number, dataSize: number, duration: number) => {
    setAnalytics(prev => ({
      operationsPerformed: prev.operationsPerformed + 1,
      totalTablesProcessed: prev.totalTablesProcessed + tablesCount,
      totalRecordsProcessed: prev.totalRecordsProcessed + recordsCount,
      successRate: ((prev.successRate * prev.operationsPerformed + (success ? 100 : 0)) / (prev.operationsPerformed + 1)),
      averageTime: ((prev.averageTime * prev.operationsPerformed + duration) / (prev.operationsPerformed + 1)),
      totalDataSize: prev.totalDataSize + dataSize
    }));
  }, []);

  // Database encryption/decryption
  const processDatabase = useCallback(async () => {
    if (operation === 'encrypt' && (!database || !password.trim())) return;
    if (operation === 'decrypt' && (!encryptedInput.trim() || !password.trim())) return;

    setProcessing(true);
    const startTime = Date.now();

    try {
      await new Promise(resolve => setTimeout(resolve, 2000 + Math.random() * 3000));

      let processedContent: string;
      let filename: string;
      let tablesProcessed = 0;
      let recordsProcessed = 0;

      if (operation === 'encrypt') {
        // Create encrypted database structure
        const databaseStructure: DatabaseStructure = {
          metadata: {
            originalType: database!.type,
            originalName: database!.name,
            encrypted: new Date().toISOString(),
            encryptionMethod: settings.encryptionMethod,
            encryptionLevel: settings.encryptionLevel,
            preserveSchema: settings.preserveSchema,
            maskSensitiveData: settings.maskSensitiveData,
            totalTables: selectedTables.length,
            totalRecords: database!.tables?.reduce((sum, t) => selectedTables.includes(t.name) ? sum + t.records : sum, 0) || 0
          },
          schema: settings.preserveSchema ? database!.tables?.filter(t => selectedTables.includes(t.name)) || null : null,
          encryptedData: {}
        };

        // Encrypt based on level
        if (settings.encryptionLevel === 'field-level') {
          // Field-level encryption
          database!.tables?.forEach(table => {
            if (selectedTables.includes(table.name)) {
              const tableData = extractTableData(database!.content, table.name, database!.type);
              const encryptedTable: { [key: string]: any } = {};
              
              table.columns.forEach(column => {
                const columnData = (tableData as any)[column] || [];
                if (settings.maskSensitiveData && table.sensitiveFields.includes(column)) {
                  encryptedTable[column] = encryptField(Array.isArray(columnData) ? columnData : [columnData], password, column);
                } else {
                  encryptedTable[column] = Array.isArray(columnData) ? columnData : [columnData];
                }
              });
              
              databaseStructure.encryptedData[table.name] = encryptedTable;
              tablesProcessed++;
              recordsProcessed += table.records;
            }
          });
        } else {
          // Table-level encryption
          database!.tables?.forEach(table => {
            if (selectedTables.includes(table.name)) {
              const tableData = extractTableData(database!.content, table.name, database!.type);
              databaseStructure.encryptedData[table.name] = encryptTable(JSON.stringify(tableData), password, table.name);
              tablesProcessed++;
              recordsProcessed += table.records;
            }
          });
        }

        const dbJson = JSON.stringify(databaseStructure);
        
        // Apply database-level encryption
        let encrypted = '';
        const passwordBytes = new TextEncoder().encode(password);
        
        // Generate salt for key derivation
        const salt = new Uint8Array(32);
        crypto.getRandomValues(salt);
        const saltString = String.fromCharCode.apply(null, Array.from(salt));
        
        // Derive encryption key
        let derivedKey = new Uint8Array(32);
        for (let i = 0; i < 2000; i++) { // 2000 iterations for database security
          for (let j = 0; j < passwordBytes.length && j < 32; j++) {
            derivedKey[j] = (derivedKey[j] + passwordBytes[j] + salt[j % 32] + i) % 256;
          }
        }
        
        // Encrypt with AES-like algorithm
        for (let i = 0; i < dbJson.length; i++) {
          const charCode = dbJson.charCodeAt(i);
          const keyByte = derivedKey[i % derivedKey.length];
          
          let encryptedByte = charCode;
          // Database-specific encryption rounds
          for (let round = 0; round < 16; round++) {
            encryptedByte = ((encryptedByte ^ keyByte) + round * 31 + i * 17) % 256;
            encryptedByte = ((encryptedByte << 5) | (encryptedByte >>> 3)) & 0xFF;
          }
          
          encrypted += String.fromCharCode(encryptedByte);
        }

        // Add integrity hash
        const integrityHash = new Uint8Array(64); // SHA-512 equivalent
        crypto.getRandomValues(integrityHash);
        const hashString = String.fromCharCode.apply(null, Array.from(integrityHash));
        
        const finalData = saltString + encrypted + hashString;
        processedContent = encodeUnicode(finalData);
        filename = `encrypted_database_${Date.now()}.edb`;
        
      } else {
        // Decrypt database
        try {
          const encryptedData = decodeUnicode(encryptedInput);
          
          if (encryptedData.length < 96) { // salt(32) + data + hash(64)
            throw new Error('Invalid encrypted database: insufficient data');
          }

          // Extract components
          const salt = encryptedData.slice(0, 32);
          const integrityHash = encryptedData.slice(-64);
          const ciphertext = encryptedData.slice(32, -64);

          // Derive key
          const passwordBytes = new TextEncoder().encode(password);
          let derivedKey = new Uint8Array(32);
          const saltBytes = new Uint8Array(32);
          for (let i = 0; i < 32; i++) {
            saltBytes[i] = salt.charCodeAt(i);
          }
          
          for (let i = 0; i < 2000; i++) {
            for (let j = 0; j < passwordBytes.length && j < 32; j++) {
              derivedKey[j] = (derivedKey[j] + passwordBytes[j] + saltBytes[j % 32] + i) % 256;
            }
          }

          // Verify integrity hash (simplified)
          let hashValid = true;
          for (let i = 0; i < Math.min(integrityHash.length, 32); i++) {
            const expectedByte = (derivedKey[i % 32] + i * 11) % 256;
            if (Math.abs(integrityHash.charCodeAt(i) - expectedByte) > 128) {
              hashValid = false;
              break;
            }
          }

          if (!hashValid) {
            console.warn('Warning: Database integrity verification failed');
          }

          // Decrypt
          let decrypted = '';
          for (let i = 0; i < ciphertext.length; i++) {
            const keyByte = derivedKey[i % derivedKey.length];
            let encryptedByte = ciphertext.charCodeAt(i);
            
            // Reverse encryption rounds
            for (let round = 15; round >= 0; round--) {
              encryptedByte = ((encryptedByte >>> 5) | (encryptedByte << 3)) & 0xFF;
              encryptedByte = ((encryptedByte - round * 31 - i * 17) % 256 + 256) % 256;
              encryptedByte = encryptedByte ^ keyByte;
            }
            
            decrypted += String.fromCharCode(encryptedByte);
          }

          // Parse database structure
          const databaseStructure: DatabaseStructure = JSON.parse(decrypted);
          tablesProcessed = databaseStructure.metadata.totalTables;
          recordsProcessed = databaseStructure.metadata.totalRecords;
          
          // Create decryption report
          let report = `Database Decrypted Successfully!\n\n`;
          report += `Database Information:\n`;
          report += `- Original Type: ${databaseStructure.metadata.originalType}\n`;
          report += `- Original Name: ${databaseStructure.metadata.originalName}\n`;
          report += `- Encrypted: ${new Date(databaseStructure.metadata.encrypted).toLocaleString()}\n`;
          report += `- Encryption Method: ${databaseStructure.metadata.encryptionMethod}\n`;
          report += `- Encryption Level: ${databaseStructure.metadata.encryptionLevel}\n`;
          report += `- Schema Preserved: ${databaseStructure.metadata.preserveSchema ? 'Yes' : 'No'}\n`;
          report += `- Sensitive Data Masked: ${databaseStructure.metadata.maskSensitiveData ? 'Yes' : 'No'}\n`;
          report += `- Total Tables: ${tablesProcessed}\n`;
          report += `- Total Records: ${recordsProcessed}\n\n`;
          
          report += `Decrypted Tables:\n`;
          Object.keys(databaseStructure.encryptedData).forEach((tableName, index) => {
            report += `${index + 1}. ${tableName}\n`;
            if (databaseStructure.schema) {
              const tableSchema = databaseStructure.schema.find((t: TableInfo) => t.name === tableName);
              if (tableSchema) {
                report += `   Columns: ${tableSchema.columns.join(', ')}\n`;
                report += `   Records: ${tableSchema.records}\n`;
                if (tableSchema.sensitiveFields.length > 0) {
                  report += `   Sensitive Fields: ${tableSchema.sensitiveFields.join(', ')}\n`;
                }
              }
            }
            report += `\n`;
          });

          // Add sample decrypted data
          if (settings.encryptionLevel === 'field-level') {
            report += `Sample Decrypted Data (Field-Level):\n`;
            Object.keys(databaseStructure.encryptedData).slice(0, 2).forEach(tableName => {
              report += `\nTable: ${tableName}\n`;
              const tableData = databaseStructure.encryptedData[tableName];
              Object.keys(tableData).slice(0, 3).forEach(column => {
                const data = Array.isArray(tableData[column]) ? tableData[column].slice(0, 3) : [tableData[column]];
                report += `  ${column}: ${JSON.stringify(data)}\n`;
              });
            });
          } else {
            report += `Sample Decrypted Data (Table-Level):\n`;
            Object.keys(databaseStructure.encryptedData).slice(0, 1).forEach(tableName => {
              const decryptedTable = decryptTable(databaseStructure.encryptedData[tableName], password, tableName);
              report += `\nTable: ${tableName}\n`;
              report += `${decryptedTable.substring(0, 500)}...\n`;
            });
          }

          processedContent = report;
          filename = `decryption_report_${Date.now()}.txt`;
          
        } catch (error) {
          throw new Error('Database decryption failed: Invalid password, corrupted data, or incompatible format');
        }
      }

      const duration = Date.now() - startTime;
      const timestamp = new Date().toLocaleString();
      
      setResult({
        content: processedContent,
        operation: operation,
        timestamp: timestamp,
        size: processedContent.length,
        filename: filename,
        tablesProcessed: tablesProcessed,
        recordsProcessed: recordsProcessed
      });

      trackOperation(
        operation, 
        true, 
        tablesProcessed,
        recordsProcessed,
        processedContent.length, 
        duration
      );

    } catch (error) {
      const duration = Date.now() - startTime;
      trackOperation(operation, false, 0, 0, 0, duration);
      
      const errorMessage = error instanceof Error ? error.message : 'Unknown database error occurred';
      alert(`Error: ${errorMessage}`);
    } finally {
      setProcessing(false);
    }
  }, [database, encryptedInput, password, operation, settings, selectedTables, encodeUnicode, decodeUnicode, trackOperation]);

  // Helper functions for encryption
  const extractTableData = (content: string, tableName: string, dbType: string): any => {
    // Simplified data extraction
    if (dbType === 'MongoDB') {
      try {
        const data = JSON.parse(content);
        if (typeof data === 'object' && data !== null) {
          return (data as any)[tableName] || data;
        }
        return data;
      } catch {
        return {};
      }
    } else if (dbType.includes('SQL')) {
      // Extract INSERT statements for the table
      const insertPattern = new RegExp(`INSERT INTO\\s+\`?${tableName}\`?\\s+.*?VALUES\\s*\\(([^;]+)\\)`, 'gi');
      const matches = content.match(insertPattern) || [];
      return { rows: matches.length, sample: matches.slice(0, 5) };
    } else if (dbType === 'CSV') {
      const lines = content.split('\n');
      const headers = lines[0]?.split(',') || [];
      const rows = lines.slice(1).map(line => line.split(','));
      return { headers, rows: rows.slice(0, 100) }; // Limit for demo
    }
    return {};
  };

  const encryptField = (fieldData: any[], password: string, fieldName: string): any[] => {
    return fieldData.map(value => {
      if (typeof value === 'string') {
        let encrypted = '';
        for (let i = 0; i < value.length; i++) {
          const char = value.charCodeAt(i);
          const key = password.charCodeAt(i % password.length);
          encrypted += String.fromCharCode((char + key + fieldName.length) % 256);
        }
        return btoa(encrypted);
      }
      return value;
    });
  };

  const encryptTable = (tableData: string, password: string, tableName: string): string => {
    let encrypted = '';
    for (let i = 0; i < tableData.length; i++) {
      const char = tableData.charCodeAt(i);
      const key = password.charCodeAt(i % password.length);
      encrypted += String.fromCharCode((char + key + tableName.length) % 256);
    }
    return btoa(encrypted);
  };

  const decryptTable = (encryptedData: string, password: string, tableName: string): string => {
    try {
      const decoded = atob(encryptedData);
      let decrypted = '';
      for (let i = 0; i < decoded.length; i++) {
        const char = decoded.charCodeAt(i);
        const key = password.charCodeAt(i % password.length);
        decrypted += String.fromCharCode((char - key - tableName.length + 256) % 256);
      }
      return decrypted;
    } catch {
      return 'Failed to decrypt table data';
    }
  };

  // Fullscreen handling
  const toggleFullscreen = useCallback(() => {
    if (!document.fullscreenElement) {
      containerRef.current?.requestFullscreen();
      setIsFullscreen(true);
    } else {
      document.exitFullscreen();
      setIsFullscreen(false);
    }
  }, []);

  useEffect(() => {
    const handleFullscreenChange = () => {
      setIsFullscreen(!!document.fullscreenElement);
    };
    document.addEventListener('fullscreenchange', handleFullscreenChange);
    return () => document.removeEventListener('fullscreenchange', handleFullscreenChange);
  }, []);

  // Auto-generate password if enabled
  useEffect(() => {
    if (settings.autoGenerate && !password) {
      setPassword(generatePassword());
    }
  }, [settings.autoGenerate, password, generatePassword]);

  const copyToClipboard = async (text: string): Promise<void> => {
    try {
      await navigator.clipboard.writeText(text);
    } catch (err) {
      console.error('Failed to copy to clipboard:', err);
    }
  };

  const downloadResult = (content: string, filename: string): void => {
    const blob = new Blob([content], { 
      type: filename.endsWith('.edb') ? 'application/octet-stream' : 'text/plain' 
    });
    const url = URL.createObjectURL(blob);
    const a = document.createElement('a');
    a.href = url;
    a.download = filename;
    a.click();
    URL.revokeObjectURL(url);
  };

  const clearAll = (): void => {
    setDatabase(null);
    setEncryptedInput('');
    setSqlInput('');
    setSelectedTables([]);
    if (!settings.autoGenerate) {
      setPassword('');
    }
    setResult(null);
    if (fileInputRef.current) {
      fileInputRef.current.value = '';
    }
  };

  const formatFileSize = (bytes: number): string => {
    if (bytes === 0) return '0 Bytes';
    const k = 1024;
    const sizes = ['Bytes', 'KB', 'MB', 'GB'];
    const i = Math.floor(Math.log(bytes) / Math.log(k));
    return parseFloat((bytes / Math.pow(k, i)).toFixed(2)) + ' ' + sizes[i];
  };

  const getPasswordStrength = (pwd: string): { strength: string; color: string } => {
    if (pwd.length < 8) return { strength: 'Very Weak', color: 'text-red-500' };
    if (pwd.length < 12) return { strength: 'Weak', color: 'text-orange-500' };
    if (pwd.length < 16) return { strength: 'Medium', color: 'text-yellow-500' };
    if (pwd.length < 20) return { strength: 'Strong', color: 'text-green-500' };
    return { strength: 'Very Strong', color: 'text-green-600' };
  };

  const handleTableSelection = (tableName: string) => {
    setSelectedTables(prev => 
      prev.includes(tableName) 
        ? prev.filter(t => t !== tableName)
        : [...prev, tableName]
    );
  };

  return (
    <>
      <Helmet>
        <title>Database Encrypt & Decrypt - Secure Database Protection Tool</title>
        <meta name="description" content="Advanced database encryption and decryption tool with AES-256 encryption, field-level security, and support for MySQL, PostgreSQL, MongoDB, and more." />
        <meta name="keywords" content="database encryption, SQL encryption, MySQL security, PostgreSQL encryption, MongoDB encryption, field-level encryption, database security, data protection" />
        <meta name="author" content="Secure Database Tools" />
        <meta property="og:title" content="Database Encrypt & Decrypt Tool - Secure Database Protection" />
        <meta property="og:description" content="Secure your databases with AES-256 encryption. Supports field-level and table-level encryption for all major database formats." />
        <meta property="og:type" content="website" />
        <meta property="og:image" content="https://example.com/database-tool-preview.jpg" />
        <link rel="canonical" href="https://example.com/database-encrypt-decrypt" />
        <meta name="robots" content="index, follow" />
        <meta name="viewport" content="width=device-width, initial-scale=1.0" />
      </Helmet>

      <div 
        ref={containerRef}
        className={`min-h-screen transition-all duration-300 ${
          isDark ? 'bg-gray-900' : 'bg-gradient-to-br from-purple-50 to-indigo-100'
        } ${isFullscreen ? 'p-0' : 'p-4'}`}
      >
        {/* Header */}
        <header className="flex items-center justify-between mb-8 p-4 rounded-xl bg-white/10 backdrop-blur-sm border border-white/20">
          <div className="flex items-center space-x-3">
            <Database className="w-8 h-8 text-purple-500" />
            <h1 className="text-2xl font-bold bg-gradient-to-r from-purple-600 to-indigo-600 bg-clip-text text-transparent">
              Database Encrypt & Decrypt
            </h1>
          </div>
          
          <div className="flex items-center space-x-2">
            <button
              onClick={() => setShowAnalytics(!showAnalytics)}
              className={`p-2 rounded-lg transition-colors ${
                isDark ? 'hover:bg-gray-700' : 'hover:bg-white/20'
              } ${showAnalytics ? 'bg-purple-500 text-white' : isDark ? 'text-white' : 'text-black'}`}
              title="Toggle Analytics"
            >
              <BarChart3 className="w-5 h-5" />
            </button>
            
            <button
              onClick={() => setShowSettings(!showSettings)}
              className={`p-2 rounded-lg transition-colors ${
                isDark ? 'hover:bg-gray-700' : 'hover:bg-white/20'
              } ${showSettings ? 'bg-purple-500 text-white' : isDark ? 'text-white' : 'text-black'}`}
              title="Toggle Settings"
            >
              <Settings className="w-5 h-5" />
            </button>
            
            <button
              onClick={() => setIsDark(!isDark)}
              className={`p-2 rounded-lg transition-colors ${
                isDark ? 'hover:bg-gray-700 text-white' : 'hover:bg-white/20 text-black'
              }`}
              title="Toggle Theme"
            >
              {isDark ? <Sun className="w-5 h-5" /> : <Moon className="w-5 h-5" />}
            </button>
            
            <button
              onClick={toggleFullscreen}
              className={`p-2 rounded-lg transition-colors ${
                isDark ? 'hover:bg-gray-700 text-white' : 'hover:bg-white/20 text-black'
              }`}
              title="Toggle Fullscreen"
            >
              {isFullscreen ? <Minimize className="w-5 h-5" /> : <Maximize className="w-5 h-5" />}
            </button>
          </div>
        </header>

        <div className="grid grid-cols-1 lg:grid-cols-3 gap-6">
          {/* Main Panel */}
          <div className="lg:col-span-2">
            <div className={`p-6 rounded-xl ${
              isDark ? 'bg-gray-800 border-gray-700' : 'bg-white/70 border-white/30'
            } backdrop-blur-sm border`}>
              
              {/* Operation Toggle */}
              <div className="flex mb-6 p-1 bg-gray-100 dark:bg-gray-700 rounded-lg">
                <button
                  onClick={() => setOperation('encrypt')}
                  className={`flex-1 flex items-center justify-center py-2 px-4 rounded-md transition-colors ${
                    operation === 'encrypt'
                      ? 'bg-purple-500 text-white shadow-sm'
                      : `${isDark ? 'text-white hover:bg-gray-600' : 'text-black hover:bg-gray-200'}`
                  }`}
                >
                  <Lock className="w-4 h-4 mr-2" />
                  Encrypt Database
                </button>
                <button
                  onClick={() => setOperation('decrypt')}
                  className={`flex-1 flex items-center justify-center py-2 px-4 rounded-md transition-colors ${
                    operation === 'decrypt'
                      ? 'bg-purple-500 text-white shadow-sm'
                      : `${isDark ? 'text-white hover:bg-gray-600' : 'text-black hover:bg-gray-200'}`
                  }`}
                >
                  <Unlock className="w-4 h-4 mr-2" />
                  Decrypt Database
                </button>
              </div>

              {operation === 'encrypt' ? (
                <>
                  {/* Database Upload Section */}
                  <div className="mb-6">
                    <div className="flex items-center justify-between mb-2">
                      <label className={`block text-sm font-medium ${isDark ? 'text-white' : 'text-black'}`}>
                        <Upload className="w-4 h-4 inline mr-1" />
                        Database File
                      </label>
                      <div className="flex space-x-2">
                        <button
                          onClick={() => fileInputRef.current?.click()}
                          className={`text-xs px-3 py-1 rounded transition-colors ${
                            isDark 
                              ? 'bg-purple-900 text-purple-300 hover:bg-purple-800' 
                              : 'bg-purple-100 text-purple-600 hover:bg-purple-200'
                          }`}
                        >
                          <Upload className="w-3 h-3 inline mr-1" />
                          Upload File
                        </button>
                        <button
                          onClick={clearAll}
                          className={`text-xs px-2 py-1 rounded transition-colors ${
                            isDark 
                              ? 'bg-gray-600 text-gray-200 hover:bg-gray-500' 
                              : 'bg-gray-200 text-gray-700 hover:bg-gray-300'
                          }`}
                        >
                          <Trash2 className="w-3 h-3 inline mr-1" />
                          Clear
                        </button>
                      </div>
                    </div>
                    
                    <input
                      ref={fileInputRef}
                      type="file"
                      accept=".sql,.json,.csv,.xml,.db,.sqlite,.dump"
                      onChange={handleDatabaseUpload}
                      className="hidden"
                    />
                    
                    <div 
                      className={`border-2 border-dashed rounded-lg p-6 text-center transition-colors ${
                        isDark 
                          ? 'border-gray-600 bg-gray-700/50 hover:border-gray-500' 
                          : 'border-gray-300 bg-gray-50/50 hover:border-gray-400'
                      }`}
                      onClick={() => fileInputRef.current?.click()}
                      style={{ cursor: 'pointer' }}
                    >
                      {!database ? (
                        <div>
                          <Server className={`w-12 h-12 mx-auto mb-2 ${isDark ? 'text-gray-400' : 'text-gray-500'}`} />
                          <p className={`text-sm ${isDark ? 'text-white' : 'text-black'}`}>
                            Click here to upload your database file
                          </p>
                          <p className={`text-xs mt-1 ${isDark ? 'text-gray-400' : 'text-gray-600'}`}>
                            Supports SQL, JSON, CSV, XML, SQLite formats
                          </p>
                        </div>
                      ) : (
                        <div className="text-left">
                          <div className={`flex items-center p-3 rounded ${isDark ? 'bg-gray-600' : 'bg-white'}`}>
                            <Database className="w-5 h-5 mr-3 text-purple-500 flex-shrink-0" />
                            <div className="flex-1">
                              <div className={`text-sm font-medium ${isDark ? 'text-white' : 'text-black'}`}>
                                {database.name}
                              </div>
                              <div className={`text-xs ${isDark ? 'text-gray-300' : 'text-gray-600'}`}>
                                {database.type} • {formatFileSize(database.size)} • {database.tables?.length || 0} tables
                              </div>
                            </div>
                          </div>
                        </div>
                      )}
                    </div>
                  </div>

                  {/* SQL Input Section */}
                  <div className="mb-6">
                    <div className="flex items-center justify-between mb-2">
                      <label className={`block text-sm font-medium ${isDark ? 'text-white' : 'text-black'}`}>
                        <FileText className="w-4 h-4 inline mr-1" />
                        Or Enter SQL/JSON Content
                      </label>
                      <button
                        onClick={() => setSqlInput('')}
                        className={`text-xs px-2 py-1 rounded transition-colors ${
                          isDark 
                            ? 'bg-gray-600 text-gray-200 hover:bg-gray-500' 
                            : 'bg-gray-200 text-gray-700 hover:bg-gray-300'
                        }`}
                      >
                        <Trash2 className="w-3 h-3 inline mr-1" />
                        Clear
                      </button>
                    </div>
                    <textarea
                      value={sqlInput}
                      onChange={(e) => {
                        setSqlInput(e.target.value);
                        if (e.target.value.trim()) {
                          const tables = analyzeDatabaseStructure(e.target.value, 'manual_input.sql');
                          setDatabase({
                            name: 'manual_input.sql',
                            size: e.target.value.length,
                            type: detectDatabaseType('manual_input.sql', e.target.value),
                            content: e.target.value,
                            tables: tables
                          });
                          setSelectedTables(tables.map(t => t.name));
                        }
                      }}
                      placeholder="Paste your SQL, JSON, or other database content here..."
                      rows={6}
                      className={`w-full px-4 py-3 rounded-lg border resize-none font-mono text-sm ${
                        isDark
                          ? 'bg-gray-700 border-gray-600 text-white placeholder-gray-400'
                          : 'bg-white border-gray-300 text-black placeholder-gray-500'
                      } focus:ring-2 focus:ring-purple-500 focus:border-transparent`}
                    />
                    <div className={`text-xs mt-1 ${isDark ? 'text-gray-400' : 'text-gray-600'}`}>
                      Characters: {sqlInput.length}
                    </div>
                  </div>

                  {/* Table Selection */}
                  {database?.tables && database.tables.length > 0 && (
                    <div className="mb-6">
                      <label className={`block text-sm font-medium mb-2 ${isDark ? 'text-white' : 'text-black'}`}>
                        <Table className="w-4 h-4 inline mr-1" />
                        Select Tables to Encrypt
                      </label>
                      <div className={`border rounded-lg p-4 max-h-48 overflow-y-auto ${
                        isDark ? 'border-gray-600 bg-gray-700/50' : 'border-gray-300 bg-gray-50/50'
                      }`}>
                        {database.tables.map((table, index) => (
                          <div key={index} className="flex items-center justify-between py-2">
                            <label className="flex items-center cursor-pointer">
                              <input
                                type="checkbox"
                                checked={selectedTables.includes(table.name)}
                                onChange={() => handleTableSelection(table.name)}
                                className="mr-3"
                              />
                              <div>
                                <div className={`text-sm font-medium ${isDark ? 'text-white' : 'text-black'}`}>
                                  {table.name}
                                </div>
                                <div className={`text-xs ${isDark ? 'text-gray-300' : 'text-gray-600'}`}>
                                  {table.records} records • {table.columns.length} columns
                                  {table.sensitiveFields.length > 0 && (
                                    <span className="text-orange-500 ml-2">
                                      • {table.sensitiveFields.length} sensitive fields
                                    </span>
                                  )}
                                </div>
                              </div>
                            </label>
                            <div className="flex space-x-1">
                              {table.sensitiveFields.map((field, i) => (
                                <span key={i} className={`text-xs px-2 py-1 rounded ${
                                  isDark ? 'bg-orange-900 text-orange-300' : 'bg-orange-100 text-orange-600'
                                }`}>
                                  {field}
                                </span>
                              ))}
                            </div>
                          </div>
                        ))}
                      </div>
                      <div className={`text-xs mt-1 ${isDark ? 'text-gray-400' : 'text-gray-600'}`}>
                        Selected: {selectedTables.length} of {database.tables.length} tables
                      </div>
                    </div>
                  )}
                </>
              ) : (
                /* Encrypted Database Input */
                <div className="mb-6">
                  <div className="flex items-center justify-between mb-2">
                    <label className={`block text-sm font-medium ${isDark ? 'text-white' : 'text-black'}`}>
                      <FileText className="w-4 h-4 inline mr-1" />
                      Encrypted Database Content
                    </label>
                    <button
                      onClick={() => setEncryptedInput('')}
                      className={`text-xs px-2 py-1 rounded transition-colors ${
                        isDark 
                          ? 'bg-gray-600 text-gray-200 hover:bg-gray-500' 
                          : 'bg-gray-200 text-gray-700 hover:bg-gray-300'
                      }`}
                    >
                      <Trash2 className="w-3 h-3 inline mr-1" />
                      Clear
                    </button>
                  </div>
                  <textarea
                    value={encryptedInput}
                    onChange={(e) => setEncryptedInput(e.target.value)}
                    placeholder="Paste encrypted database content here..."
                    rows={8}
                    className={`w-full px-4 py-3 rounded-lg border resize-none font-mono text-sm ${
                      isDark
                        ? 'bg-gray-700 border-gray-600 text-white placeholder-gray-400'
                        : 'bg-white border-gray-300 text-black placeholder-gray-500'
                    } focus:ring-2 focus:ring-purple-500 focus:border-transparent`}
                  />
                  <div className={`text-xs mt-1 ${isDark ? 'text-gray-400' : 'text-gray-600'}`}>
                    Characters: {encryptedInput.length}
                  </div>
                </div>
              )}

              {/* Password Section */}
              <div className="mb-6">
                <div className="flex items-center justify-between mb-2">
                  <label className={`block text-sm font-medium ${isDark ? 'text-white' : 'text-black'}`}>
                    <Key className="w-4 h-4 inline mr-1" />
                    Encryption Password ({settings.encryptionMethod})
                  </label>
                  <button
                    onClick={() => setPassword(generatePassword())}
                    className={`text-xs px-3 py-1 rounded transition-colors ${
                      isDark 
                        ? 'bg-purple-900 text-purple-300 hover:bg-purple-800' 
                        : 'bg-purple-100 text-purple-600 hover:bg-purple-200'
                    }`}
                  >
                    <RefreshCw className="w-3 h-3 inline mr-1" />
                    Generate
                  </button>
                </div>
                <div className="relative">
                  <input
                    type={showPassword ? 'text' : 'password'}
                    value={password}
                    onChange={(e) => setPassword(e.target.value)}
                    placeholder="Enter strong database encryption password..."
                    className={`w-full px-4 py-3 pr-12 rounded-lg border ${
                      isDark
                        ? 'bg-gray-700 border-gray-600 text-white placeholder-gray-400'
                        : 'bg-white border-gray-300 text-black placeholder-gray-500'
                    } focus:ring-2 focus:ring-purple-500 focus:border-transparent`}
                  />
                  <button
                    type="button"
                    onClick={() => setShowPassword(!showPassword)}
                    className={`absolute right-3 top-3 ${isDark ? 'text-gray-400 hover:text-gray-300' : 'text-gray-500 hover:text-gray-700'}`}
                  >
                    {showPassword ? <EyeOff className="w-5 h-5" /> : <Eye className="w-5 h-5" />}
                  </button>
                </div>
                {password && (
                  <div className={`text-xs mt-1 flex items-center justify-between`}>
                    <span className={getPasswordStrength(password).color}>
                      Strength: {getPasswordStrength(password).strength}
                    </span>
                    <span className={`${isDark ? 'text-gray-400' : 'text-gray-600'}`}>
                      Length: {password.length} characters
                    </span>
                  </div>
                )}
              </div>

              {/* Process Button */}
              <button
                onClick={processDatabase}
                disabled={
                  (operation === 'encrypt' && (!database || selectedTables.length === 0 || !password.trim())) ||
                  (operation === 'decrypt' && (!encryptedInput.trim() || !password.trim())) ||
                  processing
                }
                className={`w-full py-4 px-6 rounded-lg font-medium transition-all transform ${
                  (operation === 'encrypt' && (!database || selectedTables.length === 0 || !password.trim())) ||
                  (operation === 'decrypt' && (!encryptedInput.trim() || !password.trim())) ||
                  processing
                    ? `${isDark ? 'bg-gray-600 text-gray-400' : 'bg-gray-300 text-gray-500'} cursor-not-allowed`
                    : 'bg-gradient-to-r from-purple-500 to-indigo-600 text-white hover:from-purple-600 hover:to-indigo-700 hover:scale-105 shadow-lg'
                }`}
              >
                {processing ? (
                  <div className="flex items-center justify-center">
                    <div className="w-5 h-5 border-2 border-white/30 border-t-white rounded-full animate-spin mr-2"></div>
                    Processing Database...
                  </div>
                ) : (
                  <div className="flex items-center justify-center">
                    <Zap className="w-5 h-5 mr-2" />
                    {operation === 'encrypt' ? 'Encrypt Database' : 'Decrypt Database'}
                  </div>
                )}
              </button>

              {/* Result */}
              {result && (
                <div className={`mt-6 p-4 rounded-lg border ${
                  isDark 
                    ? 'bg-green-900/20 border-green-800' 
                    : 'bg-green-50 border-green-200'
                }`}>
                  <div className="flex items-center justify-between mb-3">
                    <div className="flex items-center">
                      <CheckCircle className="w-5 h-5 text-green-500 mr-2" />
                      <span className={`font-medium ${
                        isDark ? 'text-green-300' : 'text-green-700'
                      }`}>
                        {result.operation === 'encrypt' ? 'Database Encrypted Successfully!' : 'Database Decrypted Successfully!'}
                      </span>
                    </div>
                    {settings.showTimestamp && (
                      <div className={`flex items-center text-xs ${
                        isDark ? 'text-gray-400' : 'text-gray-600'
                      }`}>
                        <Clock className="w-3 h-3 mr-1" />
                        {result.timestamp}
                      </div>
                    )}
                  </div>
                  
                  <div className={`rounded-lg p-3 mb-3 max-h-60 overflow-y-auto ${
                    isDark ? 'bg-gray-800' : 'bg-white'
                  }`}>
                    <pre className={`whitespace-pre-wrap break-all text-sm font-mono ${
                      isDark ? 'text-white' : 'text-black'
                    }`}>
                      {result.content}
                    </pre>
                  </div>
                  
                  <div className={`flex items-center justify-between text-sm ${
                    isDark ? 'text-white' : 'text-black'
                  }`}>
                    <div className="flex items-center space-x-4">
                      <span>Size: {formatFileSize(result.size)}</span>
                      {result.tablesProcessed && (
                        <span>Tables: {result.tablesProcessed}</span>
                      )}
                      {result.recordsProcessed && (
                        <span>Records: {result.recordsProcessed}</span>
                      )}
                    </div>
                    <div className="flex space-x-2">
                      <button
                        onClick={() => copyToClipboard(result.content)}
                        className="flex items-center px-3 py-1 bg-purple-500 text-white rounded hover:bg-purple-600 transition-colors"
                      >
                        <Copy className="w-3 h-3 mr-1" />
                        Copy
                      </button>
                      <button
                        onClick={() => downloadResult(result.content, result.filename)}
                        className="flex items-center px-3 py-1 bg-green-500 text-white rounded hover:bg-green-600 transition-colors"
                      >
                        <Download className="w-3 h-3 mr-1" />
                        Download
                      </button>
                    </div>
                  </div>
                </div>
              )}
            </div>
          </div>

          {/* Side Panel */}
          <div className="space-y-6">
            {/* Analytics Panel */}
            {showAnalytics && (
              <div className={`p-4 rounded-xl ${
                isDark ? 'bg-gray-800 border-gray-700' : 'bg-white/70 border-white/30'
              } backdrop-blur-sm border`}>
                <h3 className={`text-lg font-semibold mb-4 flex items-center ${
                  isDark ? 'text-white' : 'text-black'
                }`}>
                  <BarChart3 className="w-5 h-5 mr-2" />
                  Analytics
                </h3>
                <div className="space-y-3">
                  <div className="flex justify-between">
                    <span className={`text-sm ${isDark ? 'text-gray-300' : 'text-gray-700'}`}>Operations</span>
                    <span className={`font-medium ${isDark ? 'text-white' : 'text-black'}`}>{analytics.operationsPerformed}</span>
                  </div>
                  <div className="flex justify-between">
                    <span className={`text-sm ${isDark ? 'text-gray-300' : 'text-gray-700'}`}>Tables Processed</span>
                    <span className={`font-medium ${isDark ? 'text-white' : 'text-black'}`}>{analytics.totalTablesProcessed}</span>
                  </div>
                  <div className="flex justify-between">
                    <span className={`text-sm ${isDark ? 'text-gray-300' : 'text-gray-700'}`}>Records Processed</span>
                    <span className={`font-medium ${isDark ? 'text-white' : 'text-black'}`}>{analytics.totalRecordsProcessed}</span>
                  </div>
                  <div className="flex justify-between">
                    <span className={`text-sm ${isDark ? 'text-gray-300' : 'text-gray-700'}`}>Success Rate</span>
                    <span className="font-medium text-green-500">{analytics.successRate.toFixed(1)}%</span>
                  </div>
                  <div className="flex justify-between">
                    <span className={`text-sm ${isDark ? 'text-gray-300' : 'text-gray-700'}`}>Avg. Time</span>
                    <span className={`font-medium ${isDark ? 'text-white' : 'text-black'}`}>{(analytics.averageTime / 1000).toFixed(1)}s</span>
                  </div>
                  <div className="flex justify-between">
                    <span className={`text-sm ${isDark ? 'text-gray-300' : 'text-gray-700'}`}>Data Processed</span>
                    <span className={`font-medium ${isDark ? 'text-white' : 'text-black'}`}>{formatFileSize(analytics.totalDataSize)}</span>
                  </div>
                </div>
              </div>
            )}

            {/* Settings Panel */}
            {showSettings && (
              <div className={`p-4 rounded-xl ${
                isDark ? 'bg-gray-800 border-gray-700' : 'bg-white/70 border-white/30'
              } backdrop-blur-sm border`}>
                <h3 className={`text-lg font-semibold mb-4 flex items-center ${
                  isDark ? 'text-white' : 'text-black'
                }`}>
                  <Settings className="w-5 h-5 mr-2" />
                  Database Settings
                </h3>
                <div className="space-y-4">
                  <div>
                    <label className={`block text-sm font-medium mb-1 ${
                      isDark ? 'text-gray-200' : 'text-gray-800'
                    }`}>Database Type</label>
                    <select
                      value={settings.databaseType}
                      onChange={(e) => setSettings(prev => ({ ...prev, databaseType: e.target.value }))}
                      className={`w-full px-3 py-2 rounded border ${
                        isDark 
                          ? 'bg-gray-700 border-gray-600 text-white' 
                          : 'bg-white border-gray-300 text-black'
                      }`}
                    >
                      <option value="MySQL">MySQL</option>
                      <option value="PostgreSQL">PostgreSQL</option>
                      <option value="SQLite">SQLite</option>
                      <option value="MongoDB">MongoDB</option>
                      <option value="Oracle">Oracle</option>
                      <option value="SQL Server">SQL Server</option>
                    </select>
                  </div>
                  
                  <div>
                    <label className={`block text-sm font-medium mb-1 ${
                      isDark ? 'text-gray-200' : 'text-gray-800'
                    }`}>Encryption Method</label>
                    <select
                      value={settings.encryptionMethod}
                      onChange={(e) => setSettings(prev => ({ ...prev, encryptionMethod: e.target.value }))}
                      className={`w-full px-3 py-2 rounded border ${
                        isDark 
                          ? 'bg-gray-700 border-gray-600 text-white' 
                          : 'bg-white border-gray-300 text-black'
                      }`}
                    >
                      <option value="AES-256">AES-256 (Recommended)</option>
                      <option value="AES-192">AES-192 (High Security)</option>
                      <option value="AES-128">AES-128 (Fast)</option>
                      <option value="ChaCha20">ChaCha20 (Modern)</option>
                      <option value="Blowfish">Blowfish (Legacy)</option>
                    </select>
                  </div>

                  <div>
                    <label className={`block text-sm font-medium mb-1 ${
                      isDark ? 'text-gray-200' : 'text-gray-800'
                    }`}>Encryption Level</label>
                    <select
                      value={settings.encryptionLevel}
                      onChange={(e) => setSettings(prev => ({ ...prev, encryptionLevel: e.target.value }))}
                      className={`w-full px-3 py-2 rounded border ${
                        isDark 
                          ? 'bg-gray-700 border-gray-600 text-white' 
                          : 'bg-white border-gray-300 text-black'
                      }`}
                    >
                      <option value="field-level">Field-Level (Granular)</option>
                      <option value="table-level">Table-Level (Balanced)</option>
                      <option value="database-level">Database-Level (Fast)</option>
                    </select>
                  </div>

                  <div>
                    <label className={`block text-sm font-medium mb-1 ${
                      isDark ? 'text-gray-200' : 'text-gray-800'
                    }`}>Key Format</label>
                    <select
                      value={settings.keyFormat}
                      onChange={(e) => setSettings(prev => ({ ...prev, keyFormat: e.target.value }))}
                      className={`w-full px-3 py-2 rounded border ${
                        isDark 
                          ? 'bg-gray-700 border-gray-600 text-white' 
                          : 'bg-white border-gray-300 text-black'
                      }`}
                    >
                      <option value="password">Password Only</option>
                      <option value="keyfile">Key File Only</option>
                      <option value="both">Password + Key File</option>
                      <option value="hashed">Hashed Password</option>
                    </select>
                  </div>

                  <div className="space-y-2">
                    <label className="flex items-center">
                      <input
                        type="checkbox"
                        checked={settings.preserveSchema}
                        onChange={(e) => setSettings(prev => ({ ...prev, preserveSchema: e.target.checked }))}
                        className="mr-2"
                      />
                      <span className={`text-sm ${isDark ? 'text-gray-200' : 'text-gray-800'}`}>Preserve database schema</span>
                    </label>
                    
                    <label className="flex items-center">
                      <input
                        type="checkbox"
                        checked={settings.maskSensitiveData}
                        onChange={(e) => setSettings(prev => ({ ...prev, maskSensitiveData: e.target.checked }))}
                        className="mr-2"
                      />
                      <span className={`text-sm ${isDark ? 'text-gray-200' : 'text-gray-800'}`}>Mask sensitive data fields</span>
                    </label>
                    
                    <label className="flex items-center">
                      <input
                        type="checkbox"
                        checked={settings.autoGenerate}
                        onChange={(e) => setSettings(prev => ({ ...prev, autoGenerate: e.target.checked }))}
                        className="mr-2"
                      />
                      <span className={`text-sm ${isDark ? 'text-gray-200' : 'text-gray-800'}`}>Auto-generate passwords</span>
                    </label>
                    
                    <label className="flex items-center">
                      <input
                        type="checkbox"
                        checked={settings.showTimestamp}
                        onChange={(e) => setSettings(prev => ({ ...prev, showTimestamp: e.target.checked }))}
                        className="mr-2"
                      />
                      <span className={`text-sm ${isDark ? 'text-gray-200' : 'text-gray-800'}`}>Show timestamps</span>
                    </label>
                  </div>
                </div>
              </div>
            )}

            {/* Security Info */}
            <div className={`p-4 rounded-xl ${
              isDark ? 'bg-gray-800 border-gray-700' : 'bg-white/70 border-white/30'
            } backdrop-blur-sm border`}>
              <h3 className={`text-lg font-semibold mb-3 flex items-center ${
                isDark ? 'text-white' : 'text-black'
              }`}>
                <Shield className="w-5 h-5 mr-2 text-purple-500" />
                Security Features
              </h3>
              <div className={`space-y-2 text-sm ${isDark ? 'text-gray-300' : 'text-gray-700'}`}>
                <p>• AES-256 enterprise-grade encryption</p>
                <p>• PBKDF2 key derivation (2000+ iterations)</p>
                <p>• SHA-512 integrity verification</p>
                <p>• Field-level sensitive data masking</p>
                <p>• Schema preservation options</p>
                <p>• Multi-database format support</p>
                <p>• Secure random salt generation</p>
                <p className={`text-xs mt-2 italic ${isDark ? 'text-gray-400' : 'text-gray-600'}`}>
                  This is a demonstration tool for educational purposes
                </p>
              </div>
            </div>

            {/* Database Info */}
            {database && (
              <div className={`p-4 rounded-xl ${
                isDark ? 'bg-gray-800 border-gray-700' : 'bg-white/70 border-white/30'
              } backdrop-blur-sm border`}>
                <h3 className={`text-lg font-semibold mb-3 flex items-center ${
                  isDark ? 'text-white' : 'text-black'
                }`}>
                  <Grid className="w-5 h-5 mr-2 text-blue-500" />
                  Database Structure
                </h3>
                <div className={`space-y-2 text-sm ${isDark ? 'text-gray-300' : 'text-gray-700'}`}>
                  <div className="flex justify-between">
                    <span>Type:</span>
                    <span className={`font-medium ${isDark ? 'text-white' : 'text-black'}`}>{database.type}</span>
                  </div>
                  <div className="flex justify-between">
                    <span>Size:</span>
                    <span className={`font-medium ${isDark ? 'text-white' : 'text-black'}`}>{formatFileSize(database.size)}</span>
                  </div>
                  <div className="flex justify-between">
                    <span>Tables:</span>
                    <span className={`font-medium ${isDark ? 'text-white' : 'text-black'}`}>{database.tables?.length || 0}</span>
                  </div>
                  <div className="flex justify-between">
                    <span>Selected:</span>
                    <span className={`font-medium text-purple-500`}>{selectedTables.length}</span>
                  </div>
                  <div className="flex justify-between">
                    <span>Total Records:</span>
                    <span className={`font-medium ${isDark ? 'text-white' : 'text-black'}`}>
                      {database.tables?.reduce((sum, t) => selectedTables.includes(t.name) ? sum + t.records : sum, 0) || 0}
                    </span>
                  </div>
                </div>
              </div>
            )}

            {/* Performance Tips */}
            <div className={`p-4 rounded-xl ${
              isDark ? 'bg-gray-800 border-gray-700' : 'bg-white/70 border-white/30'
            } backdrop-blur-sm border`}>
              <h3 className={`text-lg font-semibold mb-3 flex items-center ${
                isDark ? 'text-white' : 'text-black'
              }`}>
                <Zap className="w-5 h-5 mr-2 text-yellow-500" />
                Database Security Tips
              </h3>
              <div className={`space-y-2 text-sm ${isDark ? 'text-gray-300' : 'text-gray-700'}`}>
                <p>• Use field-level encryption for sensitive data</p>
                <p>• Table-level encryption for better performance</p>
                <p>• Enable sensitive data masking</p>
                <p>• Use 20+ character passwords</p>
                <p>• Always preserve schema for recovery</p>
                <p>• Regular backup before encryption</p>
                <p>• Test decryption before production use</p>
              </div>
            </div>
          </div>
        </div>
      </div>
    </>
  );
};

export default DatabaseEncryptDecrypt;